// ProfileInfoEditor.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import StatefulImage from '../component/StatefulImage.jsx';

import SlideInFromRight from '../style/SlideInFromRight.js';
import SlideOutToRight from '../style/SlideOutToRight.js';

import LoadingImage from '../../img/loading.png';
import BackIcon from '../../img/ic_topbar_back.svg';
import Spinning from '../style/Spinning.js';

import CoverPhotoPlaceHolder from '../../img/placeholder-bg-logowall.svg';
import AvatarPlaceHolder from '../../img/placeholder-avatar-cube-60.svg';
import cameraButton from '../../img/btn-chatroom-camera.svg';

import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
  ModalId,
} from '../resource/mixpanel.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

const limitLength = 150;

export class ProfileInfoEditor extends React.PureComponent {
  componentDidMount() {
    this.descriptionElement?.addEventListener('keydown', this.textareaResize);
    this.textareaResize();

    trackMixpanel({
      type: EventTypes.VIEW_OPENED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        modalId: ModalId.Popup.MyProfileEdit,
      },
    });
  }

  componentWillUnmount() {
    this.descriptionElement?.removeEventListener(
      'keydown',
      this.textareaResize
    );
    const { backgroundDraft, pictureDraft } = this.props;
    const blobRegex = /^blob:/;
    backgroundDraft.match(blobRegex) !== null &&
      URL.revokeObjectURL(backgroundDraft);
    pictureDraft.match(blobRegex) !== null && URL.revokeObjectURL(pictureDraft);
  }

  //https://stackoverflow.com/a/25621277
  textareaResize = () => {
    if (this.descriptionElement) {
      this.descriptionElement.style.cssText = 'height: auto;';
      this.descriptionElement.style.cssText = `height: ${this.descriptionElement.scrollHeight}px`;
    }
  };
  goBack = () => {
    const {
      biographyIsChanged,
      pictureIsChanged,
      backgroundIsChanged,
      openDraftDropAlert,
      closeInfoEditor,
    } = this.props;
    const isChanged =
      biographyIsChanged || pictureIsChanged || backgroundIsChanged;
    if (isChanged) {
      openDraftDropAlert();
    } else {
      trackMixpanel({
        type: EventTypes.USER_CLICKED,
        payload: {
          viewId: getViewId({ pathname: window.location.pathname }),
          buttonId: ButtonId.All.ButtonBack,
        },
      });
      closeInfoEditor();
    }
  };
  render() {
    const {
      t,
      username,
      meBackground,
      mePicture,
      meBiography,
      backgroundDraft,
      pictureDraft,
      biographyDraft,
      remoteBackground,
      remotePicture,
      status,
      isBiographyFetching,
      openProfileImageUploadModal,
      submitProfileInfo,
    } = this.props;
    return (
      <StyledProfileInfoEditor {...{ status }}>
        <CoverSection>
          <CoverPhoto>
            <StatefulImage>
              <CoverPhotoWrapper data-key="loading">
                <CoverPhotoLoadingImg src={LoadingImage} />
              </CoverPhotoWrapper>
              <CoverPhotoWrapper data-key="error">
                <CoverPhotoDefaultImg src={CoverPhotoPlaceHolder} />
              </CoverPhotoWrapper>
              <Image
                data-key="target"
                src={backgroundDraft || meBackground || remoteBackground}
              />
            </StatefulImage>
          </CoverPhoto>
          <CoverPhotoGradientOverlay />
          <CoverPhotoUploadButton
            onClick={() => openProfileImageUploadModal({ type: 'background' })}
          />
        </CoverSection>
        <Header>
          <HeaderLeft>
            <GoBackLink onClick={this.goBack}>
              <img src={BackIcon} title="close" />
            </GoBackLink>
          </HeaderLeft>
          <HeaderTitle>
            {t('myprofile_info_edit', {
              ns: TranslationNamespace.SETTINGS,
            })}
          </HeaderTitle>
          <HeaderRight>
            {isBiographyFetching ? (
              <SaveButton>
                <img src={LoadingImage} alt="loading" />
              </SaveButton>
            ) : (
              <SaveButton onClick={submitProfileInfo}>
                {t('save', {
                  ns: TranslationNamespace.GENERAL,
                })}
              </SaveButton>
            )}
          </HeaderRight>
        </Header>
        <Body>
          <UserInfoSection>
            <AvatarSection>
              <AvatarContainer>
                <StatefulImage>
                  <AvatarWrapper data-key="loading">
                    <AvatarLoadingImg src={LoadingImage} />
                  </AvatarWrapper>
                  <AvatarWrapper data-key="error">
                    <AvatarDefaultImg src={AvatarPlaceHolder} />
                  </AvatarWrapper>
                  <AvatarWrapper data-key="target">
                    <Image src={pictureDraft || mePicture || remotePicture} />
                  </AvatarWrapper>
                </StatefulImage>
                <StyledUploadButton
                  onClick={() =>
                    openProfileImageUploadModal({ type: 'picture' })
                  }
                />
              </AvatarContainer>
            </AvatarSection>
            <UsernameSection>
              <Username>{username}</Username>
              <DescriptionsLimitLength>
                <span>{biographyDraft?.length || meBiography?.length}</span>/
                <span>{limitLength}</span>
              </DescriptionsLimitLength>
            </UsernameSection>
            <Descriptions>
              <DescriptionValue
                value={
                  biographyDraft != null
                    ? biographyDraft
                    : meBiography != null
                    ? meBiography
                    : ''
                }
                ref={el => (this.descriptionElement = el)}
                onChange={({ target: { value } }) => {
                  if (value?.length <= limitLength) {
                    this.textareaResize();
                    this.props.setProfileInfoDraft({
                      field: 'biography',
                      value: value?.replace(/[\b]/g, ' '),
                    });
                  }
                }}
              />
            </Descriptions>
          </UserInfoSection>
        </Body>
      </StyledProfileInfoEditor>
    );
  }
}

ProfileInfoEditor.propTypes = {
  isBiographyFetching: PropTypes.bool,
  remoteBackground: PropTypes.string,
  remotePicture: PropTypes.string,
  username: PropTypes.string,
  meBackground: PropTypes.string,
  mePicture: PropTypes.string,
  meBiography: PropTypes.string,
  backgroundDraft: PropTypes.string,
  pictureDraft: PropTypes.string,
  biographyDraft: PropTypes.string,
  userId: PropTypes.string,
  closeInfoEditor: PropTypes.func,
  openProfileImageUploadModal: PropTypes.func,
  openDraftDropAlert: PropTypes.func,
  setProfileInfoDraft: PropTypes.func,
  submitProfileInfo: PropTypes.func,
  status: PropTypes.string,
  biographyIsChanged: PropTypes.bool,
  pictureIsChanged: PropTypes.bool,
  backgroundIsChanged: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

ProfileInfoEditor.defaultProps = {
  isBiographyFetching: false,
  userId: '',
  status: 'display',
  remoteBackground: '',
  remotePicture: '',
  username: '',
  meBackground: '',
  mePicture: '',
  meBiography: null,
  backgroundDraft: '',
  pictureDraft: '',
  biographyDraft: null,
  closeInfoEditor: () => null,
  openProfileImageUploadModal: () => null,
  openDraftDropAlert: () => null,
  setProfileInfoDraft: () => null,
  submitProfileInfo: () => null,
};

const StyledProfileInfoEditor = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  animation: 0.6s forwards
    ${({ status }) =>
      status === 'closing' ? SlideOutToRight : SlideInFromRight};
  background-color: #232323;
`;

const Header = styled.div`
  flex: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 64px;
  font-size: 16px;
  font-weight: normal;
`;

const HeaderLeft = styled.div`
  position: absolute;
  top: 50%;
  left: 16px;
  display: flex;
  align-items: center;
  transform: translateY(-50%);
  font-size: 16px;
`;

const HeaderRight = styled.div`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  font-size: 16px;
`;

const HeaderTitle = styled.div``;

const GoBackLink = styled.div`
  flex: none;
  display: block;
  height: 32px;
  :hover {
    opacity: 0.7;
  }
  & > img {
    height: 100%;
    vertical-align: top;
  }
  cursor: pointer;
`;

const SaveButton = styled.div`
  cursor: pointer;
  font-size: 16px;
  padding-right: 16px;
  line-height: 1;
  color: rgb(0, 210, 190);

  & > img {
    width: 30px;
    height: 30px;
    animation: 1s infinite linear ${Spinning};
  }
`;

const Body = styled.div`
  width: 100%;
  height: 100%;
`;

const UsernameSection = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Username = styled.div`
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  &::before {
    content: '@';
  }
`;

const AvatarSection = styled.div`
  position: relative;
  width: 100%;
  height: 72px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: start;
`;

const CoverPhoto = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const CoverPhotoWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CoverPhotoLoadingImg = styled.img`
  position: relative;
  width: auto;
  height: 50px;
  animation: 3s infinite ${Spinning};
`;

const CoverPhotoDefaultImg = styled.img`
  position: relative;
  width: 100%;
`;

const CoverPhotoGradientOverlay = styled.div`
  position: absolute;
  z-index: 0;
  top: 0;
  left: 0;
  opacity: 0.4;
  width: 100%;
  height: 80px;
  background-image: linear-gradient(to top, rgba(0, 0, 0, 0), rgb(0, 0, 0));
`;

const StyledUploadButton = styled.div`
  z-index: 1;
  position: absolute;
  display: block;
  cursor: pointer;
  bottom: 0px;
  right: 0;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background: rgb(100, 100, 100) url(${cameraButton}) no-repeat center;
  background-size: 20px;
  input {
    position: relative;
    opacity: 0;
    width: 100%;
    height: 100%;
  }
`;

const CoverSection = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 200px;
  width: 100%;
  z-index: -1;
`;

const CoverPhotoUploadButton = styled(StyledUploadButton)`
  transform: translate(-16px, -52px);
`;

const UserInfoSection = styled.div`
  position: relative;
  width: 100%;
  padding: 16px;
  margin: 0 auto;
  margin-top: 160px;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  background-color: #232323;
`;

const DescriptionsLimitLength = styled.div`
  position: relative;
  font-size: 12px;
  line-height: 1;
  margin-top: 16px;
  margin-bottom: 12px;
  color: rgb(100, 100, 100);
  span {
    margin: 0 2px;
  }
`;

const Descriptions = styled.div`
  position: relative;
  width: 100%;
  margin-top: 12px;
`;

const DescriptionValue = styled.textarea`
  position: relative;
  width: 100%;
  height: auto;
  min-height: 120px;
  border-radius: 2px;
  background-color: rgba(155, 155, 155, 0.1);
  font-size: 16px;
  line-height: 1.5;
  color: rgb(255, 255, 255);
  resize: none;
  padding: 12px 8px;
  outline: none;
  border: none;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarContainer = styled.div`
  z-index: 1;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background-color: rgb(40, 40, 40);
`;

const AvatarWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
`;

const AvatarLoadingImg = styled.img`
  position: relative;
  top: calc(50% - 25px);
  left: calc(50% - 25px);
  width: 50px;
  height: 50px;
  animation: 3s infinite ${Spinning};
`;

const AvatarDefaultImg = styled.img`
  position: relative;
  width: 100%;
`;

export default withTranslation()(ProfileInfoEditor);
